'use client';

import { useCallback, useLayoutEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useScript } from '../useScript';
import type { ResetRecaptchaParams } from './useGoogleRecaptcha';
import {
    isCheckboxRendered,
    recaptchaString,
    resetRecaptchaValues,
    setRecaptchaValues,
} from './helpers';

export function useGoogleRecaptchaCheckbox() {
    const recaptchaKeyCheckbox =
        process.env.NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY_CHECKBOX || '';

    const recaptchaSrcCheckbox = `https://www.google.com/recaptcha/enterprise.js?render=explicit`;
    const recaptchaStatusCheckbox = useScript(recaptchaSrcCheckbox);
    const [recaptchaIdCheckbox, setRecaptchaIdCheckbox] = useState<number>();

    const [useRecaptchaCheckbox, setUseRecaptchaCheckbox] = useState<
        string | null
    >(null);
    const [isRecaptchaCompleteCheckbox, setIsRecaptchaCompleteCheckbox] =
        useState<boolean>(false);
    const [recaptchaErrorCheckbox, setRecaptchaErrorCheckbox] = useState<
        string | null
    >(null);

    const pathname = usePathname();
    useLayoutEffect(() => {
        // effect removes recaptcha checkbox on url change.  Attempts before and after changing urls
        setUseRecaptchaCheckbox(null);

        return () => {
            setUseRecaptchaCheckbox(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- prevent re-render
    }, [pathname]);

    const resetRecaptchaCheckbox = useCallback(
        async (reason?: ResetRecaptchaParams) => {
            if (reason === 'failed') {
                setRecaptchaErrorCheckbox(recaptchaString.checkboxFail);
            }

            await resetRecaptchaValues(recaptchaIdCheckbox);
            setIsRecaptchaCompleteCheckbox(false);
        },
        [recaptchaIdCheckbox]
    );

    /**
     * render recaptcha checkbox
     */
    const renderRecaptchaCheckbox = useCallback(
        async (id: string) => {
            if (
                recaptchaStatusCheckbox === 'ready' &&
                !isCheckboxRendered(id)
            ) {
                const recaptchaId = window.grecaptcha?.enterprise.render(id, {
                    sitekey: recaptchaKeyCheckbox,
                    // on success
                    callback: async (recaptchaToken: string) => {
                        await setRecaptchaValues(recaptchaToken, true);
                        setIsRecaptchaCompleteCheckbox(true);
                        setRecaptchaErrorCheckbox(null);
                    },
                    // on expired
                    'expired-callback': () => {
                        resetRecaptchaCheckbox('expired');
                    },
                    // on failure
                    'error-callback': () => {
                        resetRecaptchaCheckbox('failed');
                    },
                });

                setRecaptchaErrorCheckbox(recaptchaString.checkboxRender);
                setRecaptchaIdCheckbox(recaptchaId);
            }
        },
        [recaptchaStatusCheckbox, recaptchaKeyCheckbox, resetRecaptchaCheckbox]
    );

    return {
        recaptchaSrcCheckbox,
        recaptchaStatusCheckbox,
        recaptchaIdCheckbox,
        useRecaptchaCheckbox,
        setUseRecaptchaCheckbox,
        renderRecaptchaCheckbox,
        isRecaptchaCompleteCheckbox,
        resetRecaptchaCheckbox,
        setRecaptchaIdCheckbox,
        recaptchaErrorCheckbox,
        setRecaptchaErrorCheckbox,
    };
}
